import { Component, Inject, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { UploadFile } from "@angular-ex/uploader";
import { Subscription } from "rxjs";
import { ImageEntity } from "../../../../shared/entities/file/image.entity";
import { BrandCampaignAddContentDialogData } from "../../../../shared/models/brand-campaign-add-content-dialog-data";
import { BrandCampaignFacebookPost } from "../../../../shared/models/brandCampaignFacebookPost";
import { UploaderHtmlTemplate } from "../../../../shared/components/file-uploader/file-uploader.component";
import { UploaderResponseError } from "../../../../shared/components/file-uploader/models/uploader-response-error";
import { AppDataPostTags } from "../../../../shared/models/appData";
import { BrandCampaign } from "../../../../shared/models/brandCampaign";
import { BrandCampaignCategory } from "../../../../shared/models/brandCampaignCategory";
import { BrandCampaignDetails } from "../../../../shared/models/brandCampaignDetail";
import { CampaignPostContentType } from "../../../../shared/enums/campaignPost.enums";
import { AppDataService } from "../../../../shared/services/api/app-data.service";
import { DialogService } from "../../../../shared/services/dialog.service";
import { NotificationService } from "../../../../shared/services/notification.service";
import { BrandCampaignPostService } from "../../shared/services/brand-campaign-post.service";
import { CarouselEditComponent } from "./carousel-edit/carousel-edit.component";
import { CarouselPreviewComponent } from "./carousel-preview/carousel-preview.component";
import { BrandCampaignContentFormPostSuggestedTextComponent } from "../brand-campaign-content-form-post-text/brand-campaign-content-form-post-suggested-text.component";
import { AnyToImageEntityMapper } from "../../../../shared/mappers/image.entity.mapper";

export interface CampaignContentFormPostCarouselDialogData
  extends BrandCampaignAddContentDialogData {
  readonly post?: BrandCampaignFacebookPost;
}

export type CampaignContentFormPostCarouselDialogResult = undefined;

@Component({
  selector: "app-campaign-content-form-post-carousel-dialog",
  templateUrl: "./campaign-content-form-post-carousel-dialog.component.html",
  styleUrls: ["./campaign-content-form-post-carousel-dialog.component.scss"],
})
export class CampaignContentFormPostCarouselDialogComponent
  implements OnInit, OnDestroy
{
  public saving = false;

  public readonly campaign: BrandCampaign;
  public readonly campaignDetails: BrandCampaignDetails;
  public readonly category: BrandCampaignCategory;
  public post: BrandCampaignFacebookPost;

  public postTags?: AppDataPostTags[];

  public readonly uploaderHtmlTemplate = UploaderHtmlTemplate.AddIcon;
  public readonly uploaderCarouselSubtitle: string;
  public readonly uploaderCarouselUrl: string;
  public readonly isNewPost: boolean;

  private imagesToUpload: UploadFile[] = [];
  private uploaderResponsesToHandle: ImageEntity[] = [];

  @ViewChild("carouselEdition")
  private carousel!: CarouselEditComponent;
  @ViewChild("carouselPreview")
  private carouselPreview!: CarouselPreviewComponent;
  @ViewChild("suggestedText")
  private suggestedText!: BrandCampaignContentFormPostSuggestedTextComponent;

  private readonly subscriptions = new Subscription();
  public readonly fileUploaderResponseMapper = AnyToImageEntityMapper;

  constructor(
    private readonly translateService: TranslateService,
    private readonly brandCampaignPostService: BrandCampaignPostService,
    private readonly notificationService: NotificationService,
    private readonly dialogService: DialogService,
    private readonly dialogRef: MatDialogRef<
      CampaignContentFormPostCarouselDialogComponent,
      CampaignContentFormPostCarouselDialogResult
    >,
    private readonly appDataService: AppDataService,
    @Inject(MAT_DIALOG_DATA) data: CampaignContentFormPostCarouselDialogData,
  ) {
    this.campaign = data.campaign;
    this.campaignDetails = data.campaignDetails;
    this.category = data.category;
    this.isNewPost = !data.post;

    if (data.post) {
      this.post = data.post.clone();
    } else {
      this.post = BrandCampaignFacebookPost.create(
        false,
        CampaignPostContentType.Carousel,
      );
    }
    this.uploaderCarouselSubtitle =
      this.translateService.instant(
        "brand.campaign.contentCalendarCarouselPost.validFileExtension",
      ) + " jpg, jpeg, png, mp4";

    this.uploaderCarouselUrl =
      this.brandCampaignPostService.getAddCampaignDetailCategoryPostFileUrl(
        this.campaign.id,
        this.campaignDetails.id,
        this.category.id,
      );
  }

  public ngOnInit(): void {
    this.appDataService.get().then((appData) => {
      this.postTags = appData.postTags ?? [];
    });
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  public onSaveContent(): void {
    this.saving = true;
    if (this.isNewPost) {
      this.createPost();
    } else {
      this.savePost();
    }
  }

  public canUploadMoreFiles(): boolean {
    return this.post.carouselSlides.length < 6;
  }

  public imageSelected(file: UploadFile): void {
    this.imagesToUpload.push(file);
  }

  public handleUploadCarouselResponse(file: ImageEntity): void {
    this.uploaderResponsesToHandle.push(file);
    if (this.imagesToUpload.length > 0) {
      if (
        this.imagesToUpload.every(
          (fileToUpload) => fileToUpload.responseStatus === 200,
        )
      ) {
        this.uploaderResponsesToHandle.map((uploadedResponseFile) =>
          this.onImagesUploaded(uploadedResponseFile),
        );
      }
    } else {
      this.onImagesUploaded(file);
    }
  }

  private onImagesUploaded(apiResponses: ImageEntity): void {
    this.post.carouselSlides.push(AnyToImageEntityMapper(apiResponses));
    this.imagesToUpload = [];
    this.uploaderResponsesToHandle = [];

    setTimeout(() => {
      this.carouselPreview.goToEnd();
      this.carousel.goToEnd();
    }, 500);
  }

  public onActionDeleteItem(file: ImageEntity): void {
    this.dialogService.confirm({
      title:
        "brand.campaign.contentCalendarCarouselPost.deleteImageConfirmTitle",
      message: "brand.campaign.contentCalendarCarouselPost.deleteImageConfirm",
      onConfirm: () => this.deletePostFile(file),
    });
  }

  private deletePostFile(file: ImageEntity): void {
    this.subscriptions.add(
      this.brandCampaignPostService
        .deleteCampaignDetailCategoryPostFile(
          this.campaign.id,
          this.campaignDetails.id,
          this.category.id,
          file.id,
        )
        .subscribe({
          next: () => {
            this.post.carouselSlides.forEach((loopFile, index) => {
              if (loopFile.id === file.id) {
                this.post.carouselSlides.splice(index, 1);
                this.carousel.moveBackFromLastSlide(
                  this.post.carouselSlides,
                  index,
                );
              }
            });
            this.notificationService.success(
              "brand.campaign.contentCalendarCarouselPost.imageDeleted",
            );
          },
          error: () => {
            this.notificationService.error(
              "brand.campaign.contentCalendarCarouselPost.imageNotDeleted",
            );
          },
        }),
    );
  }

  public createPost(): void {
    this.saving = true;
    this.subscriptions.add(
      this.brandCampaignPostService
        .createCampaignDetailCategoryPost(
          this.campaign.id,
          this.campaignDetails.id,
          this.category.id,
          this.post,
        )
        .subscribe({
          next: (post) => {
            this.post = post;
            this.category.posts.push(this.post);
            this.dialogRef.close();
            this.notificationService.success("shared.changesSavedSuccessfully");
          },
          error: () => {
            this.saving = false;
            this.notificationService.error("shared.changesCouldNotBeSaved");
          },
        }),
    );
  }

  private savePost(): void {
    this.saving = true;
    this.subscriptions.add(
      this.brandCampaignPostService
        .updateCampaignDetailCategoryPost(
          this.campaign.id,
          this.campaignDetails.id,
          this.category.id,
          this.post,
        )
        .subscribe({
          next: (updatedPost) => {
            this.post = updatedPost;
            this.category.posts = this.category.posts.map((post) =>
              post.id === updatedPost.id ? updatedPost : post,
            );
            this.dialogRef.close();
            this.notificationService.success("shared.changesSavedSuccessfully");
          },
          error: () => {
            this.saving = false;
            this.notificationService.error("shared.changesCouldNotBeSaved");
          },
        }),
    );
  }

  public checkSaveDisabled(): boolean {
    if (
      !this.post.postToFacebook ||
      this.post.carouselSlides.length === 0 ||
      !this.post.text ||
      this.saving
    ) {
      return true;
    }

    return this.suggestedText ? !this.suggestedText.isValid : !this.post.text;
  }

  public get showPreviewButton(): boolean {
    return !!(
      this.post.postToFacebook &&
      (this.post.carouselSlides.length > 0 || this.post.text)
    );
  }

  public onThumbnailSelected(index: number): void {
    this.carousel.moveToSlide(index);
  }

  public openPreviewModal(): Promise<void> {
    return this.dialogService.showPostPreview({
      post: this.post,
    });
  }

  public fileUploaderErrorHandler(error: UploaderResponseError): void {
    this.notificationService.error(
      this.translateService.instant(error.message, error.interpolateParams),
    );
  }

  public onTextChanged(text: string): void {
    this.post.text = text;
  }
}
